import { KEY_TRANSITIONS } from '../../Helpers/keyRegisterUtils';
import { KeyEventData, keyEventLabel, KeyEventType, KeyStatus, RegisterKey } from '../../types/keyRegister';
import type { KeyStatusFilter } from './keyRegisterView';

/** "Nr 12", or "Utan nummer" for a key without serial. */
export const keySerialLabel = (key: Pick<RegisterKey, 'serial'>): string =>
    key.serial ? `Nr ${key.serial}` : 'Utan nummer';

/** "1 nyckel" / "3 nycklar". */
export const keyCountLabel = (count: number): string => `${count} ${count === 1 ? 'nyckel' : 'nycklar'}`;

const overdueWord = (count: number): string => (count === 1 ? 'försenad' : 'försenade');

/** "1 försenad" / "2 försenade". */
export const overdueCountLabel = (count: number): string => `${count} ${overdueWord(count)}`;

/** "1 nyckel är försenad." / "2 nycklar är försenade." */
export const overdueKeysSentence = (count: number): string => `${keyCountLabel(count)} är ${overdueWord(count)}.`;

/** Plural status names for the summary cards and their legend. */
export const KEY_STATUS_PLURAL_LABELS: Record<KeyStatus, string> = {
    inStock: 'Inne',
    handedOut: 'Ute',
    lost: 'Försvunna',
    blocked: 'Spärrade',
    scrapped: 'Kasserade',
};

/** Heading of the key table for the active status filter ("Alla nycklar" without one). */
const STATUS_FILTER_TITLES: Record<KeyStatusFilter, string> = {
    inStock: 'Nycklar inne',
    out: 'Nycklar ute',
    overdue: 'Försenade nycklar',
    lost: 'Försvunna nycklar',
    blocked: 'Spärrade nycklar',
    scrapped: 'Kasserade nycklar',
};

export const keyTableTitle = (filter: KeyStatusFilter | null): string =>
    filter ? STATUS_FILTER_TITLES[filter] : 'Alla nycklar';

/** "1 försvunnen" / "2 försvunna". */
export const lostCountLabel = (count: number): string => `${count} ${count === 1 ? 'försvunnen' : 'försvunna'}`;

export const keyRegisterLink = (keyKey: string): string => `/keys?key=${encodeURIComponent(keyKey)}`;

/** How a history entry's holder attaches to its label: "Utlämnad till X", "Återlämnad av X", "Försvunnen hos X". */
const HOLDER_PREPOSITION: Partial<Record<KeyEventType, string>> = {
    handedOut: 'till',
    returned: 'av',
    lost: 'hos',
    blocked: 'hos',
    scrapped: 'hos',
};

/** A history entry as a sentence: the label plus, when the event names a holder, "till/av/hos" before the name. A comment is just "Kommentar". */
export const logHeadline = (
    event: Pick<KeyEventData, 'type' | 'dueDate' | 'holder'>,
): { label: string; preposition?: string } => ({
    label: keyEventLabel(event),
    preposition: event.holder && event.type !== 'comment' ? HOLDER_PREPOSITION[event.type] : undefined,
});

/** The status an event leaves the key in (`created` → inStock), from the transition table; null for a comment. */
export const eventResultStatus = (type: KeyEventType): KeyStatus | null =>
    type === 'created' ? 'inStock' : KEY_TRANSITIONS[type].to;

/** CSS modifier for a history marker: the colour of the status the event leaves, or a neutral ring for a comment. */
export const logMarkerClass = (type: KeyEventType): string => {
    const status = eventResultStatus(type);
    return status ? `key-bar-seg--${status}` : 'key-log-marker--comment';
};

/** Initials for the holder avatar: first letters of the first two words ("Anna Berg" → "AB"). */
export const holderInitials = (name: string | undefined): string => {
    if (!name) return '';
    return name
        .trim()
        .split(/\s+/)
        .filter(word => /\p{L}|\d/u.test(word.charAt(0)))
        .slice(0, 2)
        .map(word => word.charAt(0).toLocaleUpperCase('sv'))
        .join('');
};

/** History entries shown before "Visa äldre". */
export const LOG_PREVIEW_COUNT = 3;
